import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Combobox, Divider, Group, Stack, UnstyledButton, useCombobox } from "@mantine/core";
import { CurrentCompany } from "@/types";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import styles from "./styles.module.css";
import { useAuth } from "@/contexts/AuthContext";
import Tooltip from "@/components/atoms/Tooltip";

interface Props {
  onChange: (item: any) => void;
}

const CompanySelect: FC<Props> = ({ onChange }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { companies, currentCompany, user } = useAuth();

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch("");
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const maxCompanyNameLength = 25;
  const companyName =
    companies.find((c: CurrentCompany) => c.companyId === currentCompany?.companyId)?.companyName ?? "";

  const displayCompanyName =
    companyName.length > maxCompanyNameLength ? `${companyName.slice(0, maxCompanyNameLength - 3)}...` : companyName;

  const options = companies
    .filter((company: CurrentCompany) => company.companyId !== currentCompany?.companyId)
    .filter((company: CurrentCompany) => company.companyName.toLowerCase().includes(search.toLowerCase().trim()))
    .map((company: CurrentCompany) => (
      <Combobox.Option style={{ color: "black" }} value={company.companyId.toString()} key={company.companyId}>
        <Group gap="10px" wrap={"nowrap"}>
          <SVGAsset name="company-icon" />
          <Tooltip disabled={company.companyName.length <= maxCompanyNameLength} label={company.companyName}>
            <UnstyledButton>
              <Group wrap={"nowrap"}>
                <Text size="md">
                  {company.companyName.length > maxCompanyNameLength
                    ? `${company.companyName.slice(0, maxCompanyNameLength - 3)}...`
                    : company.companyName}
                </Text>
                {company.companyName.length > maxCompanyNameLength && <SVGAsset name={"info"} />}
              </Group>
            </UnstyledButton>
          </Tooltip>
        </Group>
      </Combobox.Option>
    ));

  return (
    <div className={styles.companySelectRoot}>
      <Combobox
        store={combobox}
        withinPortal={true}
        radius={0}
        middlewares={{
          flip: false,
          shift: false,
        }}
        zIndex={"100"}
        onOptionSubmit={(val) => {
          combobox.closeDropdown();
          onChange(val);
        }}
      >
        <Combobox.Target>
          <UnstyledButton onClick={() => combobox.toggleDropdown()} className={styles.button}>
            <Group wrap={"nowrap"}>
              <SVGAsset name="user-white" />
              <Tooltip label={companyName}>
                <Stack gap={0}>
                  <Text size="sm" strong truncate={"end"}>
                    {displayCompanyName}
                  </Text>
                  <Text size="sm">
                    {!user?.firstname && !user?.lastname && user?.email}
                    {user?.firstname} {user?.lastname}
                  </Text>
                </Stack>
              </Tooltip>
            </Group>
            <Combobox.Chevron />
          </UnstyledButton>
        </Combobox.Target>

        <Combobox.Dropdown>
          {options.length > 4 && (
            <Combobox.Search
              value={search}
              size="md"
              onChange={(event) => setSearch(event.currentTarget.value)}
              placeholder={t("commonComponents:searchByCompanyName")}
              rightSection={<SVGAsset name="search" />}
            />
          )}
          <Combobox.Options style={{ maxHeight: 400, overflowY: "auto" }}>
            {options.length > 0 ? options : <Combobox.Empty>{t("commonComponents:noCompaniesFound")}</Combobox.Empty>}
            <div className={styles.extraOptions}>
              <Divider key="divider" />
              <Combobox.Option style={{ color: "black" }} value="settings" key="settings">
                <Group gap="10px">
                  <SVGAsset name="cogs" />
                  <Text size="md">{t("common:header.settings")}</Text>
                </Group>
              </Combobox.Option>
              <Combobox.Option style={{ color: "black" }} value="logout" key="logout">
                <Group gap="10px">
                  <SVGAsset name="door-with-arrow" />
                  <Text size="md">{t("common:header.logout")}</Text>
                </Group>
              </Combobox.Option>
            </div>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
};

export default CompanySelect;
